import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Bug, Sparkles, Plus, Trash2, Edit3, Loader2, ArrowLeft, Camera, ShieldAlert, CheckCircle } from "lucide-react";
import { Link, Navigate } from "react-router-dom";
import { BBC_LOGO_URL } from "@/lib/brand";
import ProfilePhotoWidget from "@/components/ProfilePhotoWidget";
import NotificationBell from "@/components/NotificationBell";

const STATUS_OPTIONS = ["Fixed/Implemented", "Ongoing Review", "Deferred", "Declined", "request for more information"];

export default function Reports() {
  const { user } = useAuth();
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Form states
  const [editingId, setEditingId] = useState(null);
  const [title, setTitle] = useState("");
  const [desc, setDesc] = useState("");
  const [kind, setKind] = useState("bug");
  const [screenshotUrl, setScreenshotUrl] = useState("");

  const isAdmin = user && (user.role === "super_admin" || user.role === "admin" || user.role === "staff");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/reports");
      setReports(data || []);
    } catch {
      setReports([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleUploadScreenshot = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const toastId = toast.loading("Uploading screenshot…");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/chat/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      setScreenshotUrl(data.url);
      toast.success("Screenshot attached!", { id: toastId });
    } catch {
      toast.error("Upload failed", { id: toastId });
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!title.trim() || !desc.trim()) return toast.error("All parameters are required");
    setSubmitting(true);
    try {
      const payload = { title: title.trim(), description: desc.trim(), kind, screenshot_url: screenshotUrl || null };
      if (editingId) {
        const prev = reports.find(r => r.id === editingId);
        await api.put(`/reports/${editingId}`, payload);
        if (prev?.status === "Fixed/Implemented") {
          toast.info("Report re-opened automatically.");
        } else {
          toast.success("Ticket saved.");
        }
      } else {
        await api.post("/reports", payload);
        toast.success("Ticket logged successfully.");
      }
      setModalOpen(false);
      clearForm();
      load();
    } catch {
      toast.error("Could not process request.");
    } finally {
      setSubmitting(false);
    }
  };

  const clearForm = () => {
    setEditingId(null);
    setTitle("");
    setDesc("");
    setKind("bug");
    setScreenshotUrl("");
  };

  const startEdit = (item) => {
    setEditingId(item.id);
    setTitle(item.title);
    setDesc(item.description);
    setKind(item.kind);
    setScreenshotUrl(item.screenshot_url || "");
    setModalOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Remove this ticket submission permanently?")) return;
    try {
      await api.delete(`/reports/${id}`);
      toast.success("Ticket deleted");
      load();
    } catch {
      toast.error("Delete failed");
    }
  };

  const changeStatus = async (id, status) => {
    try {
      await api.put(`/reports/${id}/status`, { status });
      toast.success(`Status updated to ${status}`);
      load();
    } catch {
      toast.error("Status update failed");
    }
  };

  const getStatusStyle = (status) => {
    if (status === "Fixed/Implemented") return "bg-green-100 text-green-800";
    if (status === "Ongoing Review") return "bg-amber-100 text-amber-800";
    if (status === "request for more information") return "bg-blue-100 text-blue-800";
    return "bg-gray-100 text-gray-800";
  };

  if (!user) return <Navigate to="/admin/login" replace />;

  return (
    <div className="min-h-screen bg-[#FAF9F6] flex flex-col h-screen overflow-hidden">
      <header className="bg-white border-b border-[#E5E0D8] flex-shrink-0">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <img src={BBC_LOGO_URL} alt="" className="w-8 h-8" />
            <span className="font-serif-display text-xl text-[#2C4C3B]">Bug & Feature Reporting</span>
          </div>
          <div className="flex gap-4 text-sm font-medium items-center">
            <Link to="/" className="text-[#5C6661] hover:text-[#2C4C3B]">Home</Link>
            <Link to="/messages" className="text-[#5C6661] hover:text-[#2C4C3B]">Messages</Link>
            <NotificationBell />
            <ProfilePhotoWidget user={user} compact />
          </div>
        </div>
      </header>

      <div className="flex-1 max-w-6xl w-full mx-auto p-4 lg:p-6 overflow-hidden flex flex-col gap-4">
        <div className="bg-white border border-[#E5E0D8] p-5 rounded-2xl flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="font-serif-display text-2xl text-[#1C201E]">System Maintenance Portal</h1>
            <p className="text-xs text-[#5C6661] mt-1">Submit logs for application layouts bugs, backend request crashes or suggest new ministry additions.</p>
          </div>
          <button onClick={() => { clearForm(); setModalOpen(true); }} className="inline-flex items-center gap-2 px-4 py-2 bg-[#2C4C3B] hover:bg-[#1A3326] text-white rounded-full text-xs font-semibold self-start sm:self-auto transition-colors">
            <Plus className="w-4 h-4" /> Create Log Ticket
          </button>
        </div>

        <div className="flex-1 bg-white border border-[#E5E0D8] rounded-2xl overflow-y-auto p-4 space-y-4">
          {loading ? (
            <div className="flex items-center justify-center py-20 text-[#5C6661]"><Loader2 className="w-5 h-5 animate-spin mr-2" /> Indexing logs…</div>
          ) : reports.length === 0 ? (
            <div className="py-20 text-center text-xs text-[#8A8F8B] italic">No bugs or feedback logged under your account matching current workspace.</div>
          ) : (
            <div className="grid md:grid-cols-2 gap-4">
              {reports.map(item => (
                <div key={item.id} className="p-4 border border-[#E5E0D8] bg-[#FAF9F6]/30 rounded-xl flex flex-col justify-between gap-3 shadow-sm hover:border-[#2C4C3B]/40 transition-colors">
                  <div>
                    <div className="flex items-center justify-between gap-2 mb-2">
                      <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wider ${item.kind === "bug" ? "bg-red-100 text-red-800" : "bg-blue-100 text-blue-800"}`}>
                        {item.kind === "bug" ? "🐛 Bug" : "✨ Feature"}
                      </span>
                      {isAdmin ? (
                        <select
                          value={item.status}
                          onChange={(e) => changeStatus(item.id, e.target.value)}
                          className={`text-xs font-bold px-2 py-1 rounded border border-black/10 focus:outline-none ${getStatusStyle(item.status)}`}
                        >
                          {STATUS_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
                        </select>
                      ) : (
                        <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${getStatusStyle(item.status)}`}>
                          {item.status}
                        </span>
                      )}
                    </div>
                    <h3 className="text-sm font-bold text-[#1C201E]">{item.title}</h3>
                    <p className="text-xs text-[#5C6661] mt-1 whitespace-pre-line leading-relaxed">{item.description}</p>

                    {item.screenshot_url && (
                      <div className="mt-3 bg-white p-1 rounded-lg border border-[#E5E0D8] max-w-[200px]">
                        <img src={item.screenshot_url.startsWith("http") ? item.screenshot_url : `${process.env.REACT_APP_BACKEND_URL || ""}${item.screenshot_url}`} className="max-h-28 object-contain rounded" alt="Evidence attachment" />
                      </div>
                    )}
                  </div>

                  <div className="border-t border-[#E5E0D8]/40 pt-2 flex items-center justify-between text-[11px] text-[#8A8F8B]">
                    <span>By {item.user_name} · {new Date(item.created_at).toLocaleDateString()}</span>
                    <div className="flex gap-2.5">
                      {user.user_id === item.user_id && (
                        <button onClick={() => startEdit(item)} className="p-1 hover:text-[#2C4C3B]" title="Edit"><Edit3 className="w-3.5 h-3.5" /></button>
                      )}
                      {(user.user_id === item.user_id || isAdmin) && (
                        <button onClick={() => handleDelete(item.id)} className="p-1 hover:text-red-600" title="Delete"><Trash2 className="w-3.5 h-3.5" /></button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* TICKET ENTRY MODAL DIALOG */}
      {modalOpen && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4 backdrop-blur-sm" onClick={() => setModalOpen(false)}>
          <form onSubmit={handleSave} onClick={e => e.stopPropagation()} className="bg-white rounded-2xl shadow-xl w-full max-w-md flex flex-col max-h-[90vh] overflow-hidden">
            <div className="p-4 border-b border-[#E5E0D8] bg-[#FAF9F6] flex justify-between items-center">
              <h3 className="font-serif-display text-xl text-[#1C201E]">{editingId ? "Modify Maintenance Ticket" : "Log Issue Ticket"}</h3>
              <button type="button" onClick={() => setModalOpen(false)} className="text-xs font-semibold text-[#5C6661] hover:text-black">Cancel</button>
            </div>

            <div className="p-4 flex-1 overflow-y-auto space-y-4">
              <div>
                <label className="block text-xs uppercase tracking-wider text-[#5C6661] font-bold mb-1.5">Log Type Category</label>
                <div className="grid grid-cols-2 gap-3">
                  <button type="button" onClick={() => setKind("bug")} className={`py-2 px-3 border text-xs font-semibold rounded-xl flex items-center justify-center gap-1.5 transition-all ${kind === "bug" ? "border-red-600 bg-red-50 text-red-700" : "border-[#E5E0D8] bg-white hover:bg-gray-50"}`}>
                    🐛 Bug Error
                  </button>
                  <button type="button" onClick={() => setKind("feature_request")} className={`py-2 px-3 border text-xs font-semibold rounded-xl flex items-center justify-center gap-1.5 transition-all ${kind === "feature_request" ? "border-blue-600 bg-blue-50 text-blue-700" : "border-[#E5E0D8] bg-white hover:bg-gray-50"}`}>
                    ✨ Feature Addition
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-xs uppercase tracking-wider text-[#5C6661] font-bold mb-1">Log Summary Title</label>
                <input required type="text" value={title} onChange={e => setTitle(e.target.value)} placeholder="Summary name of ticket…" className="w-full px-3 py-2 text-sm border border-[#E5E0D8] rounded-xl focus:outline-none focus:ring-2 focus:ring-[#2C4C3B]" />
              </div>

              <div>
                <label className="block text-xs uppercase tracking-wider text-[#5C6661] font-bold mb-1">Detailed Description</label>
                <textarea required rows={4} value={desc} onChange={e => setDesc(e.target.value)} placeholder="Provide information outlining crash reproduction logs, hardware configurations or desired ministry request outlines…" className="w-full px-3 py-2 text-sm border border-[#E5E0D8] rounded-xl focus:outline-none focus:ring-2 focus:ring-[#2C4C3B]" />
              </div>

              <div>
                <label className="block text-xs uppercase tracking-wider text-[#5C6661] font-bold mb-1">Attach Screenshot Evidence</label>
                {screenshotUrl ? (
                  <div className="relative group border border-[#E5E0D8] p-1.5 bg-gray-50 rounded-xl max-w-full">
                    <img src={screenshotUrl.startsWith("http") ? screenshotUrl : `${process.env.REACT_APP_BACKEND_URL || ""}${screenshotUrl}`} className="max-h-40 mx-auto object-contain rounded-lg" alt="" />
                    <button type="button" onClick={() => setScreenshotUrl("")} className="absolute top-2 right-2 bg-white text-red-600 rounded-full p-1 border shadow shadow-black/10 hover:bg-red-50 text-xs">Remove</button>
                  </div>
                ) : (
                  <label className="border-2 border-dashed border-[#2C4C3B]/50 hover:bg-[#2C4C3B]/5 cursor-pointer rounded-xl h-24 flex flex-col items-center justify-center text-xs text-[#2C4C3B] font-semibold transition-colors">
                    <Camera className="w-5 h-5 mb-1" />
                    <span>Choose file to upload screenshot</span>
                    <input type="file" accept="image/*" onChange={handleUploadScreenshot} className="hidden" />
                  </label>
                )}
              </div>
            </div>

            <div className="p-4 border-t border-[#E5E0D8] bg-[#FAF9F6] text-right">
              <button disabled={submitting || !title.trim() || !desc.trim()} type="submit" className="px-5 py-2.5 bg-[#2C4C3B] hover:bg-[#1A3326] text-white rounded-full text-xs font-bold shadow disabled:opacity-40 flex items-center ml-auto">
                {submitting && <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" />} Submit Log Ticket
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
